import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { dialogConfig } from '../../dialogs';
import { MatIcon } from '../mat-icon/mat-icon';
import {
  DEFAULT_SCANNER_FORMATS,
  loadZXingReader,
  readBarcodesFromImageData,
  ScannerBarcodeFormat,
} from './zxing-loader';

export type BarcodeScanMode = 'single' | 'continuous';

export interface BarcodeScannerDialogData {
  /** Dialog header, already translated. */
  header?: string;
  /** Barcode formats to look for. Defaults to {@link DEFAULT_SCANNER_FORMATS}. */
  formats?: ScannerBarcodeFormat[];
  /**
   * 'single' closes the dialog with the first scanned code.
   * 'continuous' keeps scanning, calls onScan for every code and closes with all codes when the user is done.
   */
  mode?: BarcodeScanMode;
  /** Called for every scanned code in continuous mode. */
  onScan?: (code: string) => void;
  /** Continuous mode: the same code is ignored again for this long (ms). */
  repeatDelay?: number;
}

type ScannerError = 'insecure' | 'permissionDenied' | 'noCamera' | 'cameraInUse' | 'loadFailed' | 'unknown';

interface CameraOption {
  deviceId: string;
  label: string;
}

// Not yet part of the TS DOM typings
type ExtendedCapabilities = MediaTrackCapabilities & {
  torch?: boolean;
  focusMode?: string[];
  zoom?: { min: number; max: number; step: number };
};

/** Camera picked by the user. */
const CAMERA_STORAGE_KEY = 'barcodeScanner.cameraId';
/** Camera picked automatically as the best back camera, so the probing only happens once per device. */
const AUTO_CAMERA_STORAGE_KEY = 'barcodeScanner.autoCameraId';
const SCAN_INTERVAL_MS = 120;
// Scan region as fractions of the video frame. Kept wide so PDF417 and 1D codes fit.
const SCAN_REGION = { x: 0.1, y: 0.2, width: 0.8, height: 0.6 };

@Component({
  selector: 'barcode-scanner-dialog',
  imports: [ButtonModule, FormsModule, MatIcon, ProgressSpinnerModule, SelectModule, SliderModule, TranslatePipe],
  templateUrl: './barcode-scanner-dialog.html',
  styleUrl: './barcode-scanner-dialog.scss',
})
export class BarcodeScannerDialog {
  private destroyRef = inject(DestroyRef);

  video = viewChild<ElementRef<HTMLVideoElement>>('video');

  readonly scanRegion = SCAN_REGION;
  readonly mode: BarcodeScanMode;
  private readonly formats: ScannerBarcodeFormat[];
  private readonly repeatDelay: number;

  starting = signal(true);
  error = signal<ScannerError | undefined>(undefined);
  cameras = signal<CameraOption[]>([]);
  selectedCameraId = signal<string | undefined>(undefined);
  torchSupported = signal(false);
  torchOn = signal(false);
  zoomRange = signal<{ min: number; max: number; step: number } | undefined>(undefined);
  zoom = signal(1);
  scannedCodes = signal<string[]>([]);
  lastCode = signal<string | undefined>(undefined);

  private stream: MediaStream | undefined;
  private canvas: HTMLCanvasElement | undefined;
  private scanTimer: ReturnType<typeof setTimeout> | undefined;
  private lastCodeTime = 0;
  private destroyed = false;
  private finished = false;
  private onVisibilityChange = () => this.handleVisibilityChange();

  constructor(
    public dialogRef: DynamicDialogRef<BarcodeScannerDialog>,
    public config: DynamicDialogConfig<BarcodeScannerDialogData>,
  ) {
    const data = config.data ?? {};
    this.mode = data.mode ?? 'single';
    this.formats = data.formats ?? DEFAULT_SCANNER_FORMATS;
    this.repeatDelay = data.repeatDelay ?? 2000;

    // Only runs in the browser, never during SSR
    afterNextRender(() => {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.init();
    });

    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      document?.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.stopCamera();
    });
  }

  /** Opens the scanner for a single code. Resolves with the code, or undefined if cancelled. */
  static scanSingle(dialog: DialogService, data: Omit<BarcodeScannerDialogData, 'mode' | 'onScan'> = {}) {
    return new Promise<string | undefined>((resolve) => {
      BarcodeScannerDialog.open(dialog, { ...data, mode: 'single' }).onClose.subscribe((code) =>
        resolve(code),
      );
    });
  }

  /** Opens the scanner for several codes in a row. Resolves with all scanned codes when the user is done. */
  static scanContinuous(dialog: DialogService, data: Omit<BarcodeScannerDialogData, 'mode'> = {}) {
    return new Promise<string[]>((resolve) => {
      BarcodeScannerDialog.open(dialog, { ...data, mode: 'continuous' }).onClose.subscribe((codes) =>
        resolve(codes ?? []),
      );
    });
  }

  static open(dialog: DialogService, data: BarcodeScannerDialogData) {
    return dialog.open(
      BarcodeScannerDialog,
      dialogConfig<BarcodeScannerDialogData>({
        header: data.header,
        closable: true,
        dismissableMask: true,
        closeOnEscape: true,
        data,
      }),
    );
  }

  private async init() {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      this.fail('insecure');
      return;
    }

    try {
      // Load the decoder in parallel with the camera start
      const readerLoaded = loadZXingReader();
      const userCameraId = this.getStoredCameraId();
      await this.startCamera(userCameraId ?? this.getStoredValue(AUTO_CAMERA_STORAGE_KEY));
      if (!userCameraId) await this.selectBestBackCamera();
      await readerLoaded;
    } catch (e) {
      if (!this.error()) this.fail('loadFailed', e);
      return;
    }

    if (!this.destroyed && !this.error()) this.scheduleScan();
  }

  private async startCamera(deviceId?: string) {
    this.stopCamera();
    this.starting.set(true);
    this.error.set(undefined);

    const size = { width: { ideal: 1920 }, height: { ideal: 1080 } };
    const video: MediaTrackConstraints = deviceId
      ? { deviceId: { exact: deviceId }, ...size }
      : { facingMode: { ideal: 'environment' }, ...size };

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: false, video });
    } catch (e) {
      // The remembered camera may be gone, fall back to the default one
      if (deviceId && (e as DOMException)?.name === 'OverconstrainedError') {
        try {
          localStorage.removeItem(CAMERA_STORAGE_KEY);
          localStorage.removeItem(AUTO_CAMERA_STORAGE_KEY);
        } catch {}
        return this.startCamera();
      }
      this.fail(this.mapCameraError(e), e);
      throw e;
    }

    if (this.destroyed) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    this.stream = stream;
    const videoElement = this.video()!.nativeElement;
    videoElement.srcObject = stream;
    await videoElement.play().catch(() => {});

    const track = stream.getVideoTracks()[0];
    this.selectedCameraId.set(track.getSettings().deviceId);
    this.torchOn.set(false);
    await this.applyTrackCapabilities(track);
    // Some Android devices only report torch/zoom once frames are flowing
    if (!this.torchSupported() || !this.zoomRange()) {
      setTimeout(() => {
        if (this.stream?.getVideoTracks()[0] === track) this.applyTrackCapabilities(track);
      }, 500);
    }

    await this.refreshCameras();
    this.starting.set(false);
  }

  private async applyTrackCapabilities(track: MediaStreamTrack) {
    const capabilities = (track.getCapabilities?.() ?? {}) as ExtendedCapabilities;
    this.torchSupported.set(!!capabilities.torch);

    if (capabilities.zoom && capabilities.zoom.max > capabilities.zoom.min) {
      this.zoomRange.set(capabilities.zoom);
      const settings = track.getSettings() as MediaTrackSettings & { zoom?: number };
      this.zoom.set(settings.zoom ?? capabilities.zoom.min);
    } else {
      this.zoomRange.set(undefined);
    }

    if (capabilities.focusMode?.includes('continuous')) {
      await track
        .applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] })
        .catch((e) => console.warn('Could not enable continuous focus', e));
    }
  }

  /**
   * Phones with several back lenses often hand out a wide-angle or depth lens for facingMode 'environment',
   * which has a fixed focus and no torch. Probe the back cameras once and remember the best one.
   */
  private async selectBestBackCamera() {
    const currentTrack = this.stream?.getVideoTracks()[0];
    if (!currentTrack) return;

    const currentCapabilities = (currentTrack.getCapabilities?.() ?? {}) as ExtendedCapabilities;
    const storedAuto = this.getStoredValue(AUTO_CAMERA_STORAGE_KEY);
    const backCameras = this.cameras().filter((camera) => /back|rear|environment/i.test(camera.label));

    if (storedAuto && backCameras.some((camera) => camera.deviceId === storedAuto)) {
      if (storedAuto !== this.selectedCameraId()) await this.startCamera(storedAuto);
      return;
    }

    const isGood = (capabilities: ExtendedCapabilities) =>
      !!capabilities.torch && !!capabilities.focusMode?.includes('continuous');
    if (backCameras.length < 2 || isGood(currentCapabilities)) {
      this.storeValue(AUTO_CAMERA_STORAGE_KEY, this.selectedCameraId());
      return;
    }

    const currentId = this.selectedCameraId();
    let best = { deviceId: currentId, score: this.scoreCamera(currentCapabilities) };

    // Most phones can only open one camera at a time
    this.stopCamera();
    this.starting.set(true);
    for (const camera of backCameras) {
      if (this.destroyed) return;
      if (camera.deviceId === currentId) continue;
      try {
        const probe = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { deviceId: { exact: camera.deviceId } },
        });
        const capabilities = (probe.getVideoTracks()[0].getCapabilities?.() ?? {}) as ExtendedCapabilities;
        probe.getTracks().forEach((track) => track.stop());
        const score = this.scoreCamera(capabilities);
        if (score > best.score) best = { deviceId: camera.deviceId, score };
        if (isGood(capabilities)) break;
      } catch (e) {
        console.warn('Could not probe camera', camera.label, e);
      }
    }

    if (this.destroyed) return;
    this.storeValue(AUTO_CAMERA_STORAGE_KEY, best.deviceId);
    await this.startCamera(best.deviceId);
  }

  private scoreCamera(capabilities: ExtendedCapabilities) {
    let score = 0;
    if (capabilities.focusMode?.includes('continuous')) score += 2;
    if (capabilities.torch) score += 2;
    if (capabilities.zoom && capabilities.zoom.max > capabilities.zoom.min) score += 1;
    return score;
  }

  private stopCamera() {
    clearTimeout(this.scanTimer);
    this.scanTimer = undefined;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = undefined;
    const videoElement = this.video()?.nativeElement;
    if (videoElement) videoElement.srcObject = null;
  }

  private async refreshCameras() {
    // Device labels are only available after camera permission has been granted
    const devices = await navigator.mediaDevices.enumerateDevices();
    this.cameras.set(
      devices
        .filter((device) => device.kind === 'videoinput')
        .map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `Camera ${index + 1}`,
        })),
    );
  }

  async selectCamera(deviceId: string) {
    if (!deviceId || deviceId === this.stream?.getVideoTracks()[0]?.getSettings().deviceId) return;
    this.storeValue(CAMERA_STORAGE_KEY, deviceId);
    try {
      await this.startCamera(deviceId);
      this.scheduleScan();
    } catch {
      // Error state is already set by startCamera
    }
  }

  async toggleTorch() {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    const on = !this.torchOn();
    try {
      await track.applyConstraints({ advanced: [{ torch: on } as MediaTrackConstraintSet] });
      this.torchOn.set(on);
    } catch (e) {
      console.warn('Could not toggle torch', e);
      this.torchSupported.set(false);
    }
  }

  async setZoom(value: number) {
    const track = this.stream?.getVideoTracks()[0];
    if (!track) return;
    this.zoom.set(value);
    await track
      .applyConstraints({ advanced: [{ zoom: value } as MediaTrackConstraintSet] })
      .catch((e) => console.warn('Could not set zoom', e));
  }

  /** Tap on the preview: trigger a new autofocus run where supported. */
  async refocus() {
    const track = this.stream?.getVideoTracks()[0];
    const capabilities = (track?.getCapabilities?.() ?? {}) as ExtendedCapabilities;
    if (!track || !capabilities.focusMode) return;
    try {
      if (capabilities.focusMode.includes('single-shot')) {
        await track.applyConstraints({ advanced: [{ focusMode: 'single-shot' } as MediaTrackConstraintSet] });
      }
      if (capabilities.focusMode.includes('continuous')) {
        await track.applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] });
      }
    } catch (e) {
      console.warn('Could not refocus', e);
    }
  }

  retry() {
    this.init();
  }

  cancel() {
    this.finish(this.mode === 'continuous' ? this.scannedCodes() : undefined);
  }

  done() {
    this.finish(this.scannedCodes());
  }

  private scheduleScan() {
    clearTimeout(this.scanTimer);
    this.scanTimer = setTimeout(() => this.scanFrame(), SCAN_INTERVAL_MS);
  }

  private async scanFrame() {
    if (this.destroyed || this.finished || !this.stream) return;

    const videoElement = this.video()?.nativeElement;
    if (videoElement && videoElement.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      try {
        const imageData = this.grabScanRegion(videoElement);
        const results = imageData ? await readBarcodesFromImageData(imageData, this.formats) : [];
        const result = results.find((r) => r.isValid && r.text);
        if (result) this.handleCode(result.text);
      } catch (e) {
        console.warn('Barcode decode failed', e);
      }
    }

    if (!this.destroyed && !this.finished && this.stream) this.scheduleScan();
  }

  private grabScanRegion(videoElement: HTMLVideoElement): ImageData | undefined {
    const { videoWidth, videoHeight } = videoElement;
    if (!videoWidth || !videoHeight) return undefined;

    const sx = Math.round(videoWidth * SCAN_REGION.x);
    const sy = Math.round(videoHeight * SCAN_REGION.y);
    const sw = Math.round(videoWidth * SCAN_REGION.width);
    const sh = Math.round(videoHeight * SCAN_REGION.height);

    this.canvas ??= document.createElement('canvas');
    if (this.canvas.width !== sw) this.canvas.width = sw;
    if (this.canvas.height !== sh) this.canvas.height = sh;

    const context = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return undefined;
    context.drawImage(videoElement, sx, sy, sw, sh, 0, 0, sw, sh);
    return context.getImageData(0, 0, sw, sh);
  }

  private handleCode(code: string) {
    const now = Date.now();
    if (code === this.lastCode() && now - this.lastCodeTime < this.repeatDelay) {
      this.lastCodeTime = now;
      return;
    }
    this.lastCodeTime = now;
    this.lastCode.set(code);
    navigator.vibrate?.(80);

    if (this.mode === 'single') {
      this.finish(code);
      return;
    }

    this.scannedCodes.update((codes) => [...codes, code]);
    this.config.data?.onScan?.(code);
  }

  private finish(result: string | string[] | undefined) {
    if (this.finished) return;
    this.finished = true;
    this.stopCamera();
    this.dialogRef.close(result);
  }

  private handleVisibilityChange() {
    if (this.finished || this.error()) return;
    if (document.hidden) {
      this.stopCamera();
    } else if (!this.stream && !this.starting()) {
      this.startCamera(this.selectedCameraId())
        .then(() => this.scheduleScan())
        .catch(() => {});
    }
  }

  private getStoredCameraId() {
    return this.getStoredValue(CAMERA_STORAGE_KEY);
  }

  private getStoredValue(key: string) {
    try {
      return localStorage.getItem(key) ?? undefined;
    } catch {
      return undefined;
    }
  }

  private storeValue(key: string, value: string | undefined) {
    try {
      if (value) localStorage.setItem(key, value);
    } catch {}
  }

  private mapCameraError(e: unknown): ScannerError {
    switch ((e as DOMException)?.name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return 'permissionDenied';
      case 'NotFoundError':
      case 'OverconstrainedError':
        return 'noCamera';
      case 'NotReadableError':
      case 'AbortError':
        return 'cameraInUse';
      default:
        return 'unknown';
    }
  }

  private fail(error: ScannerError, cause?: unknown) {
    if (cause) console.error('Barcode scanner error', cause);
    this.stopCamera();
    this.starting.set(false);
    this.error.set(error);
  }
}
